import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import 'react-datepicker/dist/react-datepicker.css';
import './GeneralInfoForm.css'; // Reuse the same CSS for floating labels
import { getCity, getBranch, getReligion, getCluster, getLocation, getUserPayGroup, getEmployeeByEmpCode, getSpecialities } from './../api/EmpMasterApi';



const GeneralInfoForm = forwardRef((props, ref) => {
     const [localFormData, localSetFormData] = useState({
          employee_code: "",
          emp_type: "",
          employee_name: "",
          designation: "",
          department: "",
          joining_date: "",
          employment_type: "",
          mobile: "",
          email: "",
          department_category: "",
          main_department: "",
          sub_department: "",
          designation_name: "",
          grade_name: "",
          position: "",
          reporting_manager_name: "",
          reporting_manager_empcode: "",
          functional_manager_name: "",
          skip_level_manager_empcode: "",
          shift_description: "",
          company: "",
          pay_group: "",
          religion: "",
          city_name: "",
          cluster: "",
          location_name: "",
          total_experience: '',
          split_shift: 'N',
          is_assessment: 'N',
          speciality: ""
     });
     // Merge local defaults with incoming props.formData so required keys
     // like `speciality` and `is_assessment` are always present.
     const formData = { ...localFormData, ...(props.formData || {}) };
     const setFormData = props.setFormData !== undefined ? props.setFormData : localSetFormData;
     // console.log('empDateOfJoining:', formData.empDateOfJoining);
     const [dropdowns, setDropdowns] = useState({
          cities: [],
          religions: [],
          clusters: [],
          locations: [],
          pay_groups: [],
          specialties: []
     });
     const [dropdownsLoading, setDropdownsLoading] = useState({
          cities: false,
          religions: false,
          clusters: false,
          locations: false,
          pay_groups: false,
          specialties: false
     });

     const [employeeCodeValidation, setEmployeeCodeValidation] = useState({
          isChecking: false,
          isValid: true,
          message: ''
     });

     // Validation state for form fields
     const [validationErrors, setValidationErrors] = useState({});

     // Expose validation method to parent component
     useImperativeHandle(ref, () => ({
          validateForm: () => {
               const errors = {};

               // Check required fields that are actually in this form
               if (!formData.employee_code?.trim()) {
                    errors.employee_code = 'Employee code is required';
               }
               if (!formData.employee_name?.trim()) {
                    errors.employee_name = 'Employee name is required';
               }
               if (!formData.emp_type) {
                    errors.emp_type = 'Employee type is required';
               }
               if (!formData.joining_date) {
                    errors.joining_date = 'Joining date is required';
               }

               setValidationErrors(errors);
               return Object.keys(errors).length === 0;
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     // Clear individual field validation when user starts typing
     const clearFieldError = (fieldName) => {
          if (validationErrors[fieldName]) {
               setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
               });
          }
     };

     const validateEmployeeCode = async (employeeCode) => {
          if (!employeeCode || employeeCode.trim() === '') {
               setEmployeeCodeValidation({
                    isChecking: false,
                    isValid: true,
                    message: ''
               });
               return;
          }

          setEmployeeCodeValidation({
               isChecking: true,
               isValid: true,
               message: 'Checking...'
          });

          try {
               const response = await getEmployeeByEmpCode(employeeCode);

               // If we get a successful response, employee exists
               if (response && (response.data || response.id || response.employee_code)) {
                    setEmployeeCodeValidation({
                         isChecking: false,
                         isValid: false,
                         message: 'Employee code already exists!'
                    });
               } else {
                    setEmployeeCodeValidation({
                         isChecking: false,
                         isValid: true,
                         message: 'Employee code is available'
                    });
               }
          } catch (error) {
               // If API returns 404 or error, employee doesn't exist (good)
               if (error.message.includes('404') || error.message.includes('not found')) {
                    setEmployeeCodeValidation({
                         isChecking: false,
                         isValid: true,
                         message: 'Employee code is available'
                    });
               } else {
                    // Other errors
                    setEmployeeCodeValidation({
                         isChecking: false,
                         isValid: true,
                         message: 'Unable to verify employee code'
                    });
                    console.error('Error validating employee code:', error);
               }
          }
     };


     // Only check employee code existence onBlur and only in Add mode
     const handleEmployeeCodeBlur = () => {
          if (props.formMode === 'add' && formData.employee_code) {
               validateEmployeeCode(formData.employee_code);
          }
     };

     // Handle employee code change
     const handleEmployeeCodeChange = (e) => {
          const value = e.target.value.replace(/\D/g, '');
          console.log(`🔄 GeneralInfo employee_code changed: ${value}`);
          setFormData({ ...formData, employee_code: value });

          // Reset validation while typing
          if (employeeCodeValidation.message && !employeeCodeValidation.isChecking) {
               setEmployeeCodeValidation({
                    isChecking: false,
                    isValid: true,
                    message: ''
               });
          }
     };

     const [isApiInProgress, setIsApiInProgress] = useState(false);
     const hasFetchedRef = useRef(false);
     const isMountedRef = useRef(true);

     const fetchDropdowns = async () => {
          // Check if component is still mounted
          if (!isMountedRef.current) {
               console.log('🔄 Component unmounted, skipping API call');
               return;
          }

          // Prevent concurrent calls
          if (isApiInProgress) {
               console.log('🔄 API call already in progress, skipping...');
               return;
          }

          console.log('🚀 fetchDropdowns STARTED');
          console.log('📋 Current formMode:', props.formMode);
          console.log('🔧 BASE_URL:', process.env.REACT_APP_BASE_URL);

          try {
               setIsApiInProgress(true);

               // Set all loading states to true
               setDropdownsLoading({
                    cities: true,
                    religions: true,
                    clusters: true,
                    locations: true,
                    pay_groups: true,
                    branches: true,
                    specialties: true
               });
               console.log('✅ Loading states set to true');

               // Use Promise.allSettled to handle individual API failures
               const results = await Promise.allSettled([
                    getCity(),
                    getReligion(),
                    getCluster(),
                    getLocation(),
                    getBranch(),
                    getUserPayGroup(),
                    getSpecialities()
               ]);

               console.log('📊 API Results Summary:', results.map((result, index) => ({
                    api: ['getCity', 'getReligion', 'getCluster', 'getLocation', 'getBranch', 'getUserPayGroup', 'getSpecialities'][index],
                    status: result.status,
                    success: result.status === 'fulfilled'
               })));

               // Check if component is still mounted before setting state
               if (!isMountedRef.current) {
                    console.log('🔄 Component unmounted during API call, skipping state update');
                    return;
               }

               // Process successful results
               const [citiesResult, religionsResult, clustersResult, locationsResult, branchesResult, payGroupsResult, specialtiesResult] = results;

               const newDropdowns = {
                    cities: citiesResult.status === 'fulfilled' && citiesResult.value?.data ? citiesResult.value.data : [],
                    religions: religionsResult.status === 'fulfilled' && religionsResult.value?.data ? religionsResult.value.data : [],
                    clusters: clustersResult.status === 'fulfilled' && clustersResult.value?.data ? clustersResult.value.data : [],
                    locations: locationsResult.status === 'fulfilled' && locationsResult.value?.data ? locationsResult.value.data : [],
                    pay_groups: payGroupsResult.status === 'fulfilled' && payGroupsResult.value?.data ? payGroupsResult.value.data : [],
                    branches: branchesResult.status === 'fulfilled' && branchesResult.value?.data ? branchesResult.value.data : [],
                    specialties: specialtiesResult && specialtiesResult.status === 'fulfilled' && specialtiesResult.value?.data ? specialtiesResult.value.data : []
               };

               console.log('📊 Processed dropdown counts:', {
                    cities: newDropdowns.cities.length,
                    religions: newDropdowns.religions.length,
                    clusters: newDropdowns.clusters.length,
                    locations: newDropdowns.locations.length,
                    pay_groups: newDropdowns.pay_groups.length,
                    branches: newDropdowns.branches.length,
                    specialties: newDropdowns.specialties.length
               });

               setDropdowns(newDropdowns);

               // Check for failed APIs
               const failedAPIs = results
                    .map((result, index) => ({
                         name: ['getCity', 'getReligion', 'getCluster', 'getLocation', 'getBranch', 'getUserPayGroup', 'getSpecialities'][index],
                         failed: result.status === 'rejected',
                         error: result.reason
                    }))
                    .filter(api => api.failed);

               if (failedAPIs.length > 0) {
                    console.warn('⚠️ Some APIs failed:', failedAPIs);
               }

               // Set loading states to false
               setDropdownsLoading({
                    cities: false,
                    religions: false,
                    clusters: false,
                    locations: false,
                    pay_groups: false,
                    branches: false,
                    specialties: false
               });
               console.log('✅ Loading states set to false - fetchDropdowns COMPLETED');

          } catch (error) {
               console.error('❌ CRITICAL ERROR in fetchDropdowns:', error);

               if (!isMountedRef.current) return;

               // Reset loading states on error
               setDropdownsLoading({
                    cities: false,
                    religions: false,
                    clusters: false,
                    locations: false,
                    pay_groups: false,
                    branches: false,
                    specialties: false
               });

               // Set empty arrays on error
               setDropdowns({
                    cities: [],
                    religions: [],
                    clusters: [],
                    locations: [],
                    pay_groups: [],
                    branches: [],
                    specialties: []
               });
               console.log('🔄 States reset due to error');
          } finally {
               setIsApiInProgress(false);
          }
     };

     // Single useEffect for component mount
     useEffect(() => {
          // console.log('🔍 GeneralInfoForm useEffect triggered');
          // console.log('🔍 hasFetchedRef.current:', hasFetchedRef.current);

          // Mark as mounted
          isMountedRef.current = true;

          // Force fetch on first mount
          if (!hasFetchedRef.current) {
               console.log('🚀 First mount - forcing fetchDropdowns');
               hasFetchedRef.current = true;

               // Call immediately
               fetchDropdowns().catch(error => {
                    console.error('❌ Failed to fetch dropdowns on mount:', error);
               });
          }

          // Cleanup function
          return () => {
               isMountedRef.current = false;
               console.log('🔄 GeneralInfoForm unmounting');
          };
     }, []);

     // Manual trigger for testing
     // const handleManualFetch = () => {
     //      console.log('🔄 Manual fetch triggered');
     //      hasFetchedRef.current = false;
     //      fetchDropdowns();
     // };

     // console.log('🔍 About to return JSX');
     // console.log('🔍 Dropdowns state:', dropdowns);
     // console.log('🔍 Loading state:', dropdownsLoading);
     // console.log('Edit formData.emp_type:', formData.emp_type);
     return (
          <div className="general-info-form">
               <div className="form-grid g-2">
                    <div className="form-floating">
                         <select
                              className={`form-select form-select-sm ${validationErrors.emp_type ? 'is-invalid' : ''}`}
                              name="emp_type"
                              id="emp_type"
                              value={formData.emp_type || ''}
                              onChange={e => {
                                   setFormData({ ...formData, emp_type: e.target.value });
                                   if (props.onEmpTypeChange) {
                                        props.onEmpTypeChange(e.target.value); // <-- Notify parent
                                   }
                              }}
                              required
                         >
                              <option value="" disabled hidden>Employee Type</option>
                              <option value="CONTRACTUAL_EMPLOYEE">CONTRACTUAL_EMPLOYEE</option>
                              <option value="DOCTOR">DOCTOR</option>
                              <option value="POOJARI">POOJARI</option>
                         </select>
                         <label htmlFor="emp_type" style={{ opacity: formData.emp_type ? 1 : 0 }}>
                              Employee Type
                         </label>
                         {validationErrors.emp_type && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.emp_type}</small>
                              </div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className={`form-control form-control-sm ${validationErrors.employee_code ? 'is-invalid' : ''}`}
                              name="employee_code"
                              id="employee_code"
                              placeholder=" "
                              value={formData.employee_code}
                              onChange={(e) => {
                                   handleEmployeeCodeChange(e); // Pass the event as before
                                   clearFieldError('employee_code');
                              }}
                              onBlur={handleEmployeeCodeBlur}
                              autoComplete="off"
                              readOnly={props.formMode === 'edit'}
                         />
                         <label htmlFor="employee_code">Employee code</label>

                         {/* Validation error */}
                         {validationErrors.employee_code && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.employee_code}</small>
                              </div>
                         )}

                         {/* Validation feedback */}
                         {employeeCodeValidation.isChecking && (
                              <div className="d-flex align-items-center mt-1">
                                   <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                   <small className="text-muted">Checking availability...</small>
                              </div>
                         )}

                         {!employeeCodeValidation.isChecking && employeeCodeValidation.message && !validationErrors.employee_code && (
                              <div className={`mt-1 ${!employeeCodeValidation.isValid ? 'text-danger' : 'text-success'}`}>
                                   <small>
                                        <i className={`fas ${!employeeCodeValidation.isValid ? 'fa-times-circle' : 'fa-check-circle'} me-1`}></i>
                                        {employeeCodeValidation.message}
                                   </small>
                              </div>
                         )}

                         {!employeeCodeValidation.isValid && !validationErrors.employee_code && (
                              <div className="invalid-feedback d-block">
                                   <small>Employee code already exists. Please choose a different code.</small>
                              </div>
                         )}
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className={`form-control form-control-sm ${validationErrors.employee_name ? 'is-invalid' : ''}`}
                              name="employee_name"
                              id="employee_name"
                              placeholder=" "
                              value={formData.employee_name}
                              onChange={(e) => {
                                   const upperValue = e.target.value.toUpperCase();
                                   setFormData({ ...formData, employee_name: upperValue });
                                   clearFieldError('employee_name');
                              }}
                              autoComplete="off"
                         />
                         <label htmlFor="employee_name">Employee name</label>
                         {validationErrors.employee_name && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.employee_name}</small>
                              </div>
                         )}
                    </div>

                    <div className="form-floating">
                         <select
                              className={`form-select form-select-sm ${validationErrors.employment_type ? 'is-invalid' : ''}`}
                              name="employment_type"
                              id="employment_type"
                              value={formData.employment_type ?? ''}
                              onChange={(e) => {
                                   setFormData({ ...formData, employment_type: e.target.value });
                                   clearFieldError('employment_type');
                              }}
                              required
                         >
                              <option value="" disabled hidden>Employment type</option>
                              <option value="Full-Time">Full-Time</option>
                              <option value="Part-Time">Part-Time</option>
                              <option value="Contract">Contract</option>
                         </select>
                         <label htmlFor="employment_type" style={{ opacity: formData.employment_type ? 1 : 0 }}>
                              Employment type
                         </label>
                         {validationErrors.employment_type && (
                              <div className="invalid-feedback d-block">
                                   <small><i className="fas fa-exclamation-circle me-1"></i>{validationErrors.employment_type}</small>
                              </div>
                         )}
                    </div>

                    <div className="row align-items-center mb-3">
                         <div className="col-auto">
                              <label style={{ fontSize: '14px', marginLeft: '5px' }} htmlFor="joining_date" className="form-label mb-0">Joining Date</label>
                         </div>
                         <div className="col">
                              <input
                                   type="date"
                                   className={`form-control form-control-sm ${validationErrors.joining_date ? 'is-invalid' : ''}`}
                                   name="joining_date"
                                   id="joining_date"
                                   value={formData.joining_date || ''}
                                   onChange={(e) => {
                                        setFormData({ ...formData, joining_date: e.target.value });
                                        clearFieldError('joining_date');
                                   }}
                                   max={new Date().toISOString().split('T')[0]}
                                   autoComplete="off"
                              />
                              {validationErrors.joining_date && (
                                   <div className="invalid-feedback d-block">
                                        <small>
                                             <i className="fas fa-exclamation-circle me-1"></i>
                                             {validationErrors.joining_date}
                                        </small>
                                   </div>
                              )}
                         </div>
                    </div>
                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              name="company"
                              id="company"
                              placeholder=" "
                              value={formData.company}
                              onChange={e => setFormData({ ...formData, company: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="company">Company</label>
                    </div>
                    <div className="form-floating">
                         <div className="form-floating">
                              <select
                                   className="form-select form-select-sm"
                                   name="pay_group"
                                   id="pay_group"
                                   value={formData.pay_group ?? ''}
                                   onChange={e => {
                                        console.log(`🔄 GeneralInfo pay_group changed: ${e.target.value}`);
                                        setFormData({ ...formData, pay_group: e.target.value });
                                   }}
                                   disabled={dropdownsLoading.pay_groups && !formData.pay_group} // Only disable if loading AND no existing value
                                   required
                              >
                                   <option value="" disabled hidden>
                                        {dropdownsLoading.pay_groups && !formData.pay_group ? 'Loading paygroups...' : 'Paygroup'}
                                   </option>

                                   {/* ✅ ALWAYS show current value if it exists - even if API fails */}
                                   {formData.pay_group && (
                                        <option key="current" value={formData.pay_group}>
                                             {formData.pay_group}  {/* ✅ Removed unnecessary "(Current)" */}
                                        </option>
                                   )}

                                   {/* Show loading state only if no current value */}
                                   {dropdownsLoading.pay_groups && !formData.pay_group && (
                                        <option value="" disabled>Loading...</option>
                                   )}

                                   {/* Show API failure message but still allow using existing value */}
                                   {!dropdownsLoading.pay_groups && (!Array.isArray(dropdowns.pay_groups) || dropdowns.pay_groups.length === 0) && !formData.pay_group && (
                                        <option value="" disabled>Failed to load options</option>
                                   )}

                                   {/* Show additional options when loaded (excluding current value to avoid duplicates) */}
                                   {!dropdownsLoading.pay_groups && Array.isArray(dropdowns.pay_groups) &&
                                        dropdowns.pay_groups
                                             .filter(payGroup => payGroup.paygroup !== formData.pay_group) // Remove duplicate
                                             .map(payGroup => (
                                                  <option key={payGroup.id} value={payGroup.paygroup}>
                                                       {payGroup.paygroup}
                                                  </option>
                                             ))
                                   }
                              </select>
                              <label htmlFor="pay_group" style={{ opacity: formData.pay_group ? 1 : 0 }}>
                                   Paygroup {dropdownsLoading.pay_groups && !formData.pay_group && <small className="text-muted">(Loading...)</small>}
                              </label>
                         </div>
                    </div>

                    <div className="form-floating">
                         <select
                              className="form-select form-select-sm"
                              name="city_name"
                              id="city_name"
                              value={formData.city_name ?? ''}
                              onChange={e => {
                                   console.log(`🔄 GeneralInfo city_name changed: ${e.target.value}`);
                                   setFormData({ ...formData, city_name: e.target.value });
                              }}
                              disabled={dropdownsLoading.cities && !formData.city_name}
                              required
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.cities && !formData.city_name ? 'Loading cities...' : 'City name'}
                              </option>

                              {/* Always preserve existing value */}
                              {formData.city_name && (
                                   <option key="current" value={formData.city_name}>
                                        {formData.city_name}
                                   </option>
                              )}

                              {/* Loading state */}
                              {dropdownsLoading.cities && !formData.city_name && (
                                   <option value="" disabled>Loading...</option>
                              )}

                              {/* API failure but allow existing value */}
                              {!dropdownsLoading.cities && (!Array.isArray(dropdowns.cities) || dropdowns.cities.length === 0) && !formData.city_name && (
                                   <option value="" disabled>Failed to load options</option>
                              )}

                              {/* Additional options */}
                              {!dropdownsLoading.cities && Array.isArray(dropdowns.cities) &&
                                   dropdowns.cities
                                        .filter(city => city.city !== formData.city_name)
                                        .map(city => (
                                             <option key={city.id} value={city.city}>
                                                  {city.city}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="city_name" style={{ opacity: formData.city_name ? 1 : 0 }}>
                              City name
                         </label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select form-select-sm"
                              name="cluster"
                              id="cluster"
                              value={formData.cluster ?? ''}
                              onChange={e => {
                                   console.log(`🔄 GeneralInfo cluster changed: ${e.target.value}`);
                                   setFormData({ ...formData, cluster: e.target.value });
                              }}
                              disabled={dropdownsLoading.clusters && !formData.cluster} // Only disable if loading AND no existing value
                              required
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.clusters && !formData.cluster ? 'Loading clusters...' : 'Cluster'}
                              </option>

                              {/* Always preserve existing value */}
                              {formData.cluster && (
                                   <option key="current" value={formData.cluster}>
                                        {formData.cluster}
                                   </option>
                              )}

                              {/* Loading state only if no current value */}
                              {dropdownsLoading.clusters && !formData.cluster && (
                                   <option value="" disabled>Loading...</option>
                              )}

                              {/* API failure but allow existing value */}
                              {!dropdownsLoading.clusters && (!Array.isArray(dropdowns.clusters) || dropdowns.clusters.length === 0) && !formData.cluster && (
                                   <option value="" disabled>Failed to load options</option>
                              )}

                              {/* Additional options (excluding current value to avoid duplicates) */}
                              {!dropdownsLoading.clusters && Array.isArray(dropdowns.clusters) &&
                                   dropdowns.clusters
                                        .filter(cluster => cluster.cluster !== formData.cluster) // Remove duplicate
                                        .map(cluster => (
                                             <option key={cluster.id} value={cluster.cluster}>
                                                  {cluster.cluster}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="cluster" style={{ opacity: formData.cluster ? 1 : 0 }}>
                              Cluster {dropdownsLoading.clusters && !formData.cluster && <small className="text-muted">(Loading...)</small>}
                         </label>
                    </div>

                    <div className="form-floating">
                         <select
                              className="form-select form-select-sm"
                              name="location_name"
                              id="location_name"
                              value={formData.location_name ?? ''}
                              onChange={e => {
                                   console.log(`🔄 GeneralInfo location_name changed: ${e.target.value}`);
                                   setFormData({ ...formData, location_name: e.target.value });
                              }}
                              disabled={dropdownsLoading.branches && !formData.location_name}
                              required
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.branches && !formData.location_name ? 'Loading branches...' : 'Branch/Location'}
                              </option>

                              {/* Always preserve existing value */}
                              {formData.location_name && (
                                   <option key="current" value={formData.location_name}>
                                        {formData.location_name}
                                   </option>
                              )}

                              {/* Loading state only if no current value */}
                              {dropdownsLoading.branches && !formData.location_name && (
                                   <option value="" disabled>Loading...</option>
                              )}

                              {/* API failure but allow existing value */}
                              {!dropdownsLoading.branches && (!Array.isArray(dropdowns.branches) || dropdowns.branches.length === 0) && !formData.location_name && (
                                   <option value="" disabled>Failed to load options</option>
                              )}

                              {/* Additional options (excluding current value to avoid duplicates) */}
                              {!dropdownsLoading.branches && Array.isArray(dropdowns.branches) &&
                                   dropdowns.branches
                                        .filter(branch => branch.SysField !== formData.location_name) // Remove duplicate
                                        .map(branch => (
                                             <option key={branch.id} value={branch.SysField}>
                                                  {branch.SysField}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="location_name" style={{ opacity: formData.location_name ? 1 : 0 }}>
                              Branch/Location {dropdownsLoading.branches && !formData.location_name && <small className="text-muted">(Loading...)</small>}
                         </label>
                    </div>

                    {/* 
                    <div className="form-floating">
                         <select
                              className="form-select form-select-sm"
                              name="location_name"
                              id="location_name"
                              value={formData.location_name ?? ''}
                              onChange={e => {
                                   console.log(`🔄 GeneralInfo location_name changed: ${e.target.value}`);
                                   setFormData({ ...formData, location_name: e.target.value });
                              }}
                              disabled={dropdownsLoading.locations && !formData.location_name} // Only disable if loading AND no existing value
                              required
                         >
                              <option value="" disabled hidden>
                                   {dropdownsLoading.locations && !formData.location_name ? 'Loading locations...' : 'Location name'}
                              </option>

                              
                              {formData.location_name && (
                                   <option key="current" value={formData.location_name}>
                                        {formData.location_name}
                                   </option>
                              )}

                              
                              {dropdownsLoading.locations && !formData.location_name && (
                                   <option value="" disabled>Loading...</option>
                              )}

                               
                              {!dropdownsLoading.locations && (!Array.isArray(dropdowns.locations) || dropdowns.locations.length === 0) && !formData.location_name && (
                                   <option value="" disabled>Failed to load options</option>
                              )}

                               
                              {!dropdownsLoading.locations && Array.isArray(dropdowns.locations) &&
                                   dropdowns.locations
                                        .filter(location => location.location !== formData.location_name) // Remove duplicate
                                        .map(location => (
                                             <option key={location.id} value={location.location}>
                                                  {location.location}
                                             </option>
                                        ))
                              }
                         </select>
                         <label htmlFor="location_name" style={{ opacity: formData.location_name ? 1 : 0 }}>
                              Location name {dropdownsLoading.locations && !formData.location_name && <small className="text-muted">(Loading...)</small>}
                         </label>
                    </div> 
                    */}
                    <div className="form-floating">
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              name="total_experience"
                              id="total_experience"
                              placeholder=" "
                              value={formData.total_experience}
                              onChange={e => setFormData({ ...formData, total_experience: e.target.value })}
                              autoComplete="off"
                         />
                         <label htmlFor="total_experience">Total Experience (years)</label>
                    </div>
                    <div className="form-floating">
                         <select
                              className="form-select form-select-sm"
                              name="split_shift"
                              id="split_shift"
                              value={formData.split_shift || 'N'}
                              onChange={e => {
                                   setFormData({ ...formData, split_shift: e.target.value });
                                   if (props.onSplitShiftChange) {
                                        props.onSplitShiftChange(e.target.value);
                                   }
                              }}
                         >
                              <option value="N">NO</option>
                              <option value="Y">YES</option>
                         </select>
                         <label htmlFor="split_shift">Split Shift</label>
                    </div>
                    {formData.emp_type === 'DOCTOR' && (
                         <>
                              {/* getSpecialities */}
                              <div className="form-floating">
                                   <select
                                        className="form-select form-select-sm"
                                        name="speciality"
                                        id="speciality"
                                        value={formData.speciality ?? ''}
                                        onChange={e => {
                                             setFormData({ ...formData, speciality: e.target.value });
                                             if (props.onSpecialtyChange) {
                                                  props.onSpecialtyChange(e.target.value);
                                             }
                                        }}
                                   >
                                        <option value="" disabled hidden>
                                             {dropdownsLoading.specialties && !formData.speciality ? 'Loading specialties...' : 'Specialty'}
                                        </option>
                                        {formData.speciality && (
                                             <option key="current" value={formData.speciality}>
                                                  {formData.speciality}
                                             </option>
                                        )}
                                        {dropdownsLoading.specialties && !formData.speciality && (
                                             <option value="" disabled>Loading...</option>
                                        )}
                                        {!dropdownsLoading.specialties && (!Array.isArray(dropdowns.specialties) || dropdowns.specialties.length === 0) && !formData.speciality && (
                                             <option value="" disabled>Failed to load options</option>
                                        )}
                                        {!dropdownsLoading.specialties && Array.isArray(dropdowns.specialties) &&
                                             dropdowns.specialties
                                                  .filter(speciality => speciality.speciality !== formData.speciality) // Remove duplicate
                                                  .map(speciality => (
                                                       <option key={speciality.id} value={speciality.speciality}>
                                                            {speciality.speciality}
                                                       </option>
                                                  ))
                                        }
                                   </select>
                                   <label htmlFor="speciality" style={{ opacity: formData.speciality ? 1 : 0 }}>
                                        Speciality {dropdownsLoading.specialties && !formData.speciality && <small className="text-muted">(Loading...)</small>}
                                   </label>
                              </div>
                              {/* is_assessment */}
                              <div className="form-floating">
                                   <select
                                        className="form-select form-select-sm"
                                        name="is_assessment"
                                        id="is_assessment"
                                        value={formData.is_assessment || 'N'}
                                        onChange={e => {
                                             setFormData({ ...formData, is_assessment: e.target.value });
                                             if (props.onAssessmentChange) {
                                                  props.onAssessmentChange(e.target.value);
                                             }
                                        }}
                                   >
                                        <option value="N">NO</option>
                                        <option value="Y">YES</option>
                                   </select>
                                   <label htmlFor="is_assessment">Is Self Assessment Allowed</label>
                              </div>
                         </>
                    )}
               </div>
          </div>
     );
});

export default GeneralInfoForm;